import { formatUnits } from 'viem'

/**
 * Number formatting, in one place.
 *
 * Every page was calling `formatUnits(v, 18)` inline. That hardcodes an assumption
 * the chain does not guarantee — a payment token with 6 decimals (real USDT on most
 * chains) would have rendered a balance a trillion times too large, with no error.
 * Decimals belong to the token and are read from it; these helpers just take them.
 */

/** Full precision down to `maxFractionDigits`, with thousands separators. */
export function formatAmount(
  value: bigint,
  decimals: number,
  maxFractionDigits = 4,
): string {
  return Number(formatUnits(value, decimals)).toLocaleString(undefined, {
    maximumFractionDigits: maxFractionDigits,
  })
}

/**
 * Display an on-chain amount without rounding it above the value that was minted.
 *
 * This keeps the calculation in strings/bigints, so large token values also avoid
 * JavaScript Number precision loss. Trailing zeroes are omitted.
 */
export function formatAmountTruncated(
  value: bigint,
  decimals: number,
  maxFractionDigits = 4,
): string {
  const [whole = '0', fraction = ''] = formatUnits(value, decimals).split('.')
  const groupedWhole = BigInt(whole).toLocaleString('en-US')
  const truncatedFraction = fraction.slice(0, maxFractionDigits).replace(/0+$/, '')
  return truncatedFraction ? `${groupedWhole}.${truncatedFraction}` : groupedWhole
}

/**
 * Abbreviated for headline figures: 1.24M, 18.3K, 942.05.
 *
 * Real amounts get compact notation only above 1,000 — below that the exact figure
 * is short enough to show and more useful than "0.9K".
 */
export function formatCompact(value: bigint, decimals: number): string {
  return compact(Number(formatUnits(value, decimals)))
}

export function compact(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) {
    // Do not round 999,999 up to 1000.00K: that visually claims the $1M target
    // has been reached. Truncate within the K unit until the value truly crosses 1M.
    const thousands = n / 1_000
    const truncated = Math.trunc(thousands * 100) / 100
    return `${truncated.toFixed(2)}K`
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

/**
 * A price, with enough decimals that a real move stays visible.
 *
 * A fixed 2 decimals would render a sub-cent token as "0.00" and every move as no
 * move at all, so precision scales with magnitude.
 */
export function formatPrice(v: number): string {
  if (!Number.isFinite(v)) return '—'
  if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (v >= 1) return v.toFixed(4)
  if (v >= 0.01) return v.toFixed(5)
  if (v > 0) return v.toPrecision(4)
  return '0'
}

/** A fraction as a signed percentage, e.g. 0.1822 → "+18.22%". */
export function formatPercent(fraction: number | null, digits = 2): string {
  if (fraction === null || !Number.isFinite(fraction)) return '—'
  return `${fraction >= 0 ? '+' : ''}${(fraction * 100).toFixed(digits)}%`
}

/** USD-style money for market cap and volume. */
export function formatUsd(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—'
  return `$${compact(n)}`
}

/** Shortened address for dense UI, e.g. 0x1F4E…ce51. */
export function shortAddress(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`
}

/** Coarse relative time — "in 89 days" beats a ticking second hand. */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return 'moments'
  const d = Math.floor(seconds / 86_400)
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'}`
  const h = Math.floor(seconds / 3_600)
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'}`
  const m = Math.max(1, Math.floor(seconds / 60))
  return `${m} minute${m === 1 ? '' : 's'}`
}
